import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Shield } from 'lucide-react';
import { AppFooter } from '@/components/layout/AppFooter';
import { BrandLogo } from '@/components/BrandLogo';

export default function PrivacyPage() {
  return (
    <div data-testid="privacy-page" className="min-h-screen bg-background">
      <nav className="border-b border-border bg-background/80 backdrop-blur">
        <div className="max-w-4xl mx-auto px-6 flex h-16 items-center gap-3">
          <BrandLogo width={180} />
        </div>
      </nav>
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: March 1, 2026 | Version 1.0</p>

        <div className="prose prose-sm max-w-none space-y-6 text-foreground">
          <section>
            <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>1. Data We Collect</h2>
            <p className="text-sm leading-relaxed text-muted-foreground">We collect account information (name, email, company), uploaded compliance documents (PDFs, images), vendor contact information, and usage analytics. Document content is processed to extract compliance-relevant data.</p>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>2. Third-Party AI Sub-Processors</h2>
            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="p-4">
                <p className="text-sm font-medium leading-relaxed">All uploaded documents (PDFs and images) are processed by the following third-party AI sub-processors:</p>
                <ul className="text-sm mt-2 space-y-1 ml-4 list-disc">
                  <li><strong>OpenAI</strong> — Used for AI-powered text extraction, document classification, and compliance field analysis.</li>
                  <li><strong>Amazon Web Services (AWS)</strong> — Used for document storage (S3), OCR text extraction (Textract), and scheduled processing (EventBridge/Lambda).</li>
                </ul>
                <p className="text-sm mt-2 leading-relaxed">These processors handle document data in accordance with their respective privacy policies and data processing agreements.</p>
              </CardContent>
            </Card>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>3. Data Retention</h2>
            <Card className="border-[hsl(36_80%_76%)] bg-[hsl(36_100%_96%)]">
              <CardContent className="p-4">
                <p className="text-sm font-medium text-[hsl(28,95%,26%)] leading-relaxed">Data is stored for the duration of the active subscription plus seven (7) years for audit purposes. This retention period ensures compliance with regulatory requirements and enables historical compliance auditing. After the retention period, data is permanently deleted from all systems including backups.</p>
              </CardContent>
            </Card>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>4. Security Measures</h2>
            <p className="text-sm leading-relaxed text-muted-foreground">We implement the following security measures to protect your data:</p>
            <ul className="text-sm space-y-2 text-muted-foreground list-disc ml-6 mt-2">
              <li><strong>SSL/TLS Encryption</strong> — All data transmitted between your browser and our servers is encrypted using industry-standard SSL/TLS protocols.</li>
              <li><strong>Role-Based Access Control (RBAC)</strong> — Access to data is restricted based on user roles (Admin, Manager, Assistant, Owner). Each role has specific permissions limiting what data can be viewed or modified.</li>
              <li><strong>Multi-Tenant Isolation</strong> — Each company's data is logically isolated using CompanyID-based query scoping, ensuring no cross-tenant data access.</li>
              <li><strong>JWT Authentication</strong> — Secure token-based authentication with automatic expiration.</li>
              <li><strong>Magic Link Tokens</strong> — Vendor portal access uses cryptographically secure, time-limited tokens with SHA-256 hashing.</li>
            </ul>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>5. Your Rights</h2>
            <p className="text-sm leading-relaxed text-muted-foreground">Depending on your jurisdiction, you may have the right to: access your personal data, request correction of inaccurate data, request deletion of your data (subject to our retention policy), opt out of certain data processing activities, and data portability.</p>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>6. Contact</h2>
            <p className="text-sm leading-relaxed text-muted-foreground">For privacy-related inquiries, contact us at help@vendorsuite.io or submit a support ticket through the Platform.</p>
          </section>
        </div>
      </div>
      <AppFooter />
    </div>
  );
}
